/**
 * one-tab-only (RESPONDENT_WIZARD_SPEC v1.3 §7, validé WARDEN).
 *
 * Garantit qu'une seule session wizard est éditée à la fois dans le même
 * navigateur :
 *  - **BroadcastChannel** (`wfsr_${sessionHash}`) — annonce/présence inter-onglets.
 *  - **Lock localStorage** (`wfsr_lock_{sessionHash}`) — fallback robustesse +
 *    détection d'abandon (TTL 15 s, refresh toutes les 5 s).
 *
 * **sessionHash dérivé du token côté JS** (`sha256(token).slice(0,8)`), JAMAIS
 * du `sessionId` serveur — évite une corrélation observable par script tiers
 * (cf. §7 v1.3, SENTINEL).
 */
import { useCallback, useEffect, useRef, useState } from 'react';

const LOCK_TTL_MS = 15_000;
const REFRESH_MS = 5_000;

interface LockPayload {
  timestamp: number;
  tabId: string;
}

interface ChannelMsg {
  type: 'hello' | 'primary' | 'takeover' | 'released';
  tabId: string;
}

/** sha256(token).slice(0,8) — dérivé WebCrypto, asynchrone. */
export async function deriveSessionHash(token: string): Promise<string> {
  const enc = new TextEncoder();
  const buf = await crypto.subtle.digest('SHA-256', enc.encode(token));
  const arr = Array.from(new Uint8Array(buf));
  return arr.map((b) => b.toString(16).padStart(2, '0')).join('').slice(0, 8);
}

export function lockKeyFor(sessionHash: string): string {
  return `wfsr_lock_${sessionHash}`;
}

export function pendingKeyFor(sessionHash: string): string {
  return `wfsr_pending_${sessionHash}`;
}

/** Supprime les entrées localStorage scopées par sessionHash (submit final / erase art. 17). */
export function clearTabState(sessionHash: string): void {
  try {
    localStorage.removeItem(lockKeyFor(sessionHash));
    localStorage.removeItem(pendingKeyFor(sessionHash));
  } catch {
    // localStorage indisponible (mode privé) — best-effort
  }
}

function readLock(key: string): { active: boolean; tabId: string | null } {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return { active: false, tabId: null };
    const parsed = JSON.parse(raw) as Partial<LockPayload>;
    if (typeof parsed.timestamp !== 'number') return { active: false, tabId: null };
    const age = Date.now() - parsed.timestamp;
    if (age > LOCK_TTL_MS) return { active: false, tabId: parsed.tabId ?? null };
    return { active: true, tabId: parsed.tabId ?? null };
  } catch {
    return { active: false, tabId: null };
  }
}

function writeLock(key: string, tabId: string): void {
  try {
    const payload: LockPayload = { timestamp: Date.now(), tabId };
    localStorage.setItem(key, JSON.stringify(payload));
  } catch {
    // best-effort
  }
}

/**
 * Hook React — état `isPrimary` + fonction `takeOver` permettant à l'onglet
 * shadow de prendre la main (UI : « Reprendre ici »). À l'unmount du primary,
 * le lock est libéré + un message `released` est broadcasté.
 */
export function useOneTabLock(sessionHash: string | null): {
  isPrimary: boolean;
  takeOver: () => void;
} {
  const [isPrimary, setIsPrimary] = useState<boolean>(true);
  const tabIdRef = useRef<string>(`t-${Math.random().toString(36).slice(2, 10)}-${Date.now()}`);
  const primaryRef = useRef<boolean>(true);
  primaryRef.current = isPrimary;
  const channelRef = useRef<BroadcastChannel | null>(null);

  useEffect(() => {
    if (!sessionHash || typeof window === 'undefined') return;
    if (typeof BroadcastChannel === 'undefined') {
      // Fallback : pas de BroadcastChannel (vieux navigateur) — on suppose
      // primary. Le lock localStorage reste actif pour détection.
      return;
    }

    const tabId = tabIdRef.current;
    const channelName = `wfsr_${sessionHash}`;
    const lockKey = lockKeyFor(sessionHash);
    const channel = new BroadcastChannel(channelName);
    channelRef.current = channel;

    // Décision initiale.
    const existing = readLock(lockKey);
    if (existing.active && existing.tabId && existing.tabId !== tabId) {
      setIsPrimary(false);
      channel.postMessage({ type: 'hello', tabId } satisfies ChannelMsg);
    } else {
      writeLock(lockKey, tabId);
      setIsPrimary(true);
      channel.postMessage({ type: 'hello', tabId } satisfies ChannelMsg);
    }

    // Refresh / surveillance.
    const interval = window.setInterval(() => {
      if (primaryRef.current) {
        writeLock(lockKey, tabId);
      } else {
        const e = readLock(lockKey);
        if (!e.active) {
          // Le primary a disparu sans broadcast (kill onglet brutal) — on prend.
          writeLock(lockKey, tabId);
          setIsPrimary(true);
        }
      }
    }, REFRESH_MS);

    channel.onmessage = (ev) => {
      const msg = ev.data as ChannelMsg;
      if (!msg || !msg.type || msg.tabId === tabId) return;
      if (msg.type === 'hello') {
        if (primaryRef.current) {
          channel.postMessage({ type: 'primary', tabId } satisfies ChannelMsg);
        }
      } else if (msg.type === 'primary' && primaryRef.current) {
        // Conflit (deux tabs se croient primary) : résolution déterministe par
        // ordre alphabétique des tabIds.
        if (msg.tabId < tabId) {
          setIsPrimary(false);
          writeLock(lockKey, msg.tabId);
        }
      } else if (msg.type === 'takeover') {
        setIsPrimary(false);
      } else if (msg.type === 'released' && !primaryRef.current) {
        writeLock(lockKey, tabId);
        setIsPrimary(true);
      }
    };

    return () => {
      // Libère si on était primary.
      if (primaryRef.current) {
        try {
          const raw = localStorage.getItem(lockKey);
          if (raw) {
            const parsed = JSON.parse(raw) as Partial<LockPayload>;
            if (parsed.tabId === tabId) {
              localStorage.removeItem(lockKey);
            }
          }
        } catch {
          // best-effort
        }
        channel.postMessage({ type: 'released', tabId } satisfies ChannelMsg);
      }
      channel.close();
      channelRef.current = null;
      window.clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionHash]);

  const takeOver = useCallback(() => {
    if (!sessionHash) return;
    const tabId = tabIdRef.current;
    const ch = channelRef.current;
    if (ch) {
      ch.postMessage({ type: 'takeover', tabId } satisfies ChannelMsg);
    }
    writeLock(lockKeyFor(sessionHash), tabId);
    setIsPrimary(true);
  }, [sessionHash]);

  return { isPrimary, takeOver };
}
